import { parseMaybeDoubleEncodedJSON } from './parseMaybeDoubleEncodedJSON';
import { firstStringValue as firstString, toStringValue } from './strings';

export interface ToolLabelData {
  label?: string;
  argsSummary?: string;
  resultSummary?: string;
  resultCount?: number;
}

export type ToolLabelProvider = {
  labelForStart: (name: string, input?: Record<string, unknown>) => ToolLabelData;
  labelForResult: (name: string, result: unknown) => ToolLabelData;
};

const registry = new Map<string, ToolLabelProvider>();

export function registerToolLabelProvider(toolName: string, provider: ToolLabelProvider): void {
  registry.set(toolName, provider);
}

export function toolLabelProviderFor(toolName: string): ToolLabelProvider {
  return registry.get(toolName) ?? defaultProvider;
}

const defaultProvider: ToolLabelProvider = {
  labelForStart: (name, input) => {
    const keys = input ? Object.keys(input) : [];
    return { label: name, argsSummary: keys.join(', ') };
  },
  labelForResult: (name, result) => {
    return defaultResultSummary(name, result);
  },
};

// Built-ins (frontend now keeps only a generic fallback; backend provides labels)

function countFromKnownArrays(obj: any): { count?: number; label?: string } {
  if (!obj || typeof obj !== 'object') return {};
  // Primary keys commonly used across tools
  const keys = ['items', 'results', 'documents', 'files', 'matches', 'rows', 'hits'];
  for (const k of keys) {
    const v = (obj as any)[k];
    if (Array.isArray(v)) {
      // Prefer domain-specific labels
      const label =
        k === 'documents' || k === 'files' ? 'documents' : k === 'matches' ? 'matches' : 'items';
      return { count: v.length, label };
    }
  }
  // Nested common container
  if (obj.data && typeof obj.data === 'object') {
    const nested = countFromKnownArrays(obj.data);
    if (nested.count !== undefined) return nested;
  }
  return {};
}

function defaultResultSummary(_name: string, result: unknown): ToolLabelData {
  const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
  if (Array.isArray(parsed)) {
    return { resultSummary: `${parsed.length} items`, resultCount: parsed.length };
  }
  if (parsed && typeof parsed === 'object') {
    const { count, label } = countFromKnownArrays(parsed);
    if (typeof count === 'number') {
      const noun = label ?? 'items';
      return { resultSummary: `${count} ${noun}`, resultCount: count };
    }
  }
  return {};
}

// drive_search_documents
registerToolLabelProvider('drive_search_documents', {
  labelForStart: (_name, input) => {
    const q = input
      ? firstString((input as any).query, (input as any).q, (input as any).text)
      : undefined;
    return { label: 'Searching documents', argsSummary: q };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const { count } = countFromKnownArrays(parsed);
      if (typeof count === 'number') {
        return { resultSummary: `${count} documents`, resultCount: count };
      }
    }
    return defaultResultSummary('drive_search_documents', result);
  },
});

// drive_fetch_document_content
registerToolLabelProvider('drive_fetch_document_content', {
  labelForStart: (_name, input) => {
    const doc = input
      ? firstString(
          (input as any).doc_id,
          (input as any).document_id,
          (input as any).documentId,
          (input as any).file_id,
          (input as any).id,
          (input as any).title
        )
      : undefined;
    return { label: 'Fetching document', argsSummary: doc };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const title = firstString((parsed as any).title, (parsed as any).name);
      if (title) return { resultSummary: title };
      const content = firstString((parsed as any).content, (parsed as any).text);
      if (content) {
        const trimmed = content.length > 60 ? content.slice(0, 57) + '…' : content;
        return { resultSummary: trimmed };
      }
    }
    return defaultResultSummary('drive_fetch_document_content', result);
  },
});

// codebase_search
registerToolLabelProvider('codebase_search', {
  labelForStart: (_name, input) => {
    const query = input ? firstString((input as any).query) : undefined;
    let dirs: string | undefined;
    const td = (input as any)?.target_directories;
    if (Array.isArray(td) && td.length > 0) {
      const items = td.map((x: unknown) => toStringValue(x)).filter(Boolean) as string[];
      if (items.length > 0) {
        dirs = `in ${items.slice(0, 3).join(', ')}${items.length > 3 ? '…' : ''}`;
      }
    }
    const argsSummary = [query, dirs].filter(Boolean).join(' ');
    return { label: 'Searching codebase', argsSummary: argsSummary || query };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const { count } = countFromKnownArrays(parsed);
      if (typeof count === 'number') {
        return { resultSummary: `${count} results`, resultCount: count };
      }
    }
    if (Array.isArray(parsed)) {
      return { resultSummary: `${parsed.length} results`, resultCount: parsed.length };
    }
    return defaultResultSummary('codebase_search', result);
  },
});

// grep
registerToolLabelProvider('grep', {
  labelForStart: (_name, input) => {
    const pat = input ? firstString((input as any).pattern, (input as any).regex) : undefined;
    const path = input ? firstString((input as any).path) : undefined;
    const glob = input ? firstString((input as any).glob) : undefined;
    const loc = path || glob ? `in ${path ?? glob}` : undefined;
    const argsSummary = [pat, loc].filter(Boolean).join(' ');
    return { label: 'Searching text patterns', argsSummary: argsSummary || pat };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const { count } = countFromKnownArrays(parsed);
      if (typeof count === 'number') {
        return { resultSummary: `${count} matches`, resultCount: count };
      }
    }
    if (Array.isArray(parsed)) {
      return { resultSummary: `${parsed.length} matches`, resultCount: parsed.length };
    }
    return defaultResultSummary('grep', result);
  },
});

// doclens
registerToolLabelProvider('doclens', {
  labelForStart: (_name, input) => {
    const q = input
      ? firstString((input as any).query, (input as any).terms, (input as any).text)
      : undefined;
    return { label: 'DocLens retrieval', argsSummary: q };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const { count } = countFromKnownArrays(parsed);
      if (typeof count === 'number') {
        return { resultSummary: `${count} documents`, resultCount: count };
      }
      const summary = firstString((parsed as any).summary, (parsed as any)?.data?.summary);
      if (summary) return { resultSummary: summary };
    }
    return defaultResultSummary('doclens', result);
  },
});

// read_file
registerToolLabelProvider('read_file', {
  labelForStart: (_name, input) => {
    const path = input
      ? firstString((input as any).path, (input as any).file, (input as any).filename)
      : undefined;
    return { label: 'Reading file', argsSummary: path };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (typeof parsed === 'string') {
      const lines = parsed.split('\n').length;
      return { resultSummary: `${lines} lines` };
    }
    return defaultResultSummary('read_file', result);
  },
});

// delete_file
registerToolLabelProvider('delete_file', {
  labelForStart: (_name, input) => {
    const path = input
      ? firstString((input as any).path, (input as any).file, (input as any).filename)
      : undefined;
    return { label: 'Deleting file', argsSummary: path };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const ok = (parsed as any).ok;
      if (typeof ok === 'boolean') return { resultSummary: ok ? 'deleted' : 'failed' };
    }
    return defaultResultSummary('delete_file', result);
  },
});

// run_workflow
registerToolLabelProvider('run_workflow', {
  labelForStart: (_name, input) => {
    const wf = input
      ? firstString((input as any).name, (input as any).workflow, (input as any).id)
      : undefined;
    return { label: 'Running workflow', argsSummary: wf };
  },
  labelForResult: (_name, result) => defaultResultSummary('run_workflow', result),
});

// run_workflow_step
registerToolLabelProvider('run_workflow_step', {
  labelForStart: (_name, input) => {
    const step = input
      ? firstString((input as any).step, (input as any).step_name, (input as any).name)
      : undefined;
    return { label: 'Executing workflow step', argsSummary: step };
  },
  labelForResult: (_name, result) => defaultResultSummary('run_workflow_step', result),
});

// team_guess
registerToolLabelProvider('team_guess', {
  labelForStart: (_name, input) => {
    const q = input
      ? firstString((input as any).query, (input as any).prompt, (input as any).text)
      : undefined;
    return { label: 'Suggesting team members', argsSummary: q };
  },
  labelForResult: (_name, result) => defaultResultSummary('team_guess', result),
});

// team_analyzer
registerToolLabelProvider('team_analyzer', {
  labelForStart: (_name, input) => {
    const q = input
      ? firstString((input as any).query, (input as any).prompt, (input as any).text)
      : undefined;
    return { label: 'Analyzing team', argsSummary: q };
  },
  labelForResult: (_name, result) => defaultResultSummary('team_analyzer', result),
});

// search_gemini_transcripts
registerToolLabelProvider('search_gemini_transcripts', {
  labelForStart: (_name, input) => {
    const q = input
      ? firstString((input as any).query, (input as any).q, (input as any).text)
      : undefined;
    return { label: 'Searching Gemini transcripts', argsSummary: q };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const { count } = countFromKnownArrays(parsed);
      if (typeof count === 'number')
        return { resultSummary: `${count} results`, resultCount: count };
    }
    return defaultResultSummary('search_gemini_transcripts', result);
  },
});

// propose_team
registerToolLabelProvider('propose_team', {
  labelForStart: (_name, input) => {
    const members = Array.isArray((input as any)?.members)
      ? ((input as any).members as unknown[])
      : undefined;
    const count = members ? members.length : undefined;
    const argsSummary = typeof count === 'number' ? `${count} members` : undefined;
    return { label: 'Proposing team', argsSummary };
  },
  labelForResult: (_name, result) => defaultResultSummary('propose_team', result),
});

// drive_fetch_document_chunked
registerToolLabelProvider('drive_fetch_document_chunked', {
  labelForStart: (_name, input) => {
    const doc = input
      ? firstString(
          (input as any).documentId,
          (input as any).document_id,
          (input as any).doc_id,
          (input as any).id
        )
      : undefined;
    return { label: 'Fetching document (chunked)', argsSummary: doc };
  },
  labelForResult: (_name, result) => {
    const parsed = parseMaybeDoubleEncodedJSON(result, { onParseError: 'undefined' });
    if (parsed && typeof parsed === 'object') {
      const chunks = (parsed as any).chunks;
      if (Array.isArray(chunks))
        return { resultSummary: `${chunks.length} chunks`, resultCount: chunks.length };
    }
    return defaultResultSummary('drive_fetch_document_chunked', result);
  },
});

// drive_process_next_chunk
registerToolLabelProvider('drive_process_next_chunk', {
  labelForStart: (_name, input) => {
    const session = input
      ? firstString((input as any).sessionId, (input as any).session_id, (input as any).session)
      : undefined;
    return { label: 'Processing next chunk', argsSummary: session };
  },
  labelForResult: (_name, result) => defaultResultSummary('drive_process_next_chunk', result),
});
